import React from "react";
import toast, { Toaster, resolveValue } from "react-hot-toast";

// Polar Night toasts (DESIGN.md "Motion" and "Components"): one fixed
// 360 px column at the bottom right, above the call dock; elevation 3
// glass with a 2 px rule on the left edge per type (accent: done, danger:
// failed, neutral: info and progress); an optional action and a close
// button; a hairline that runs down the toast's life and pauses while the
// pointer is over the column (react-hot-toast pauses its timers then too).
// Rise in on --sg-slow, leave to the right on --sg-exit-slow.
//
// Callers keep using react-hot-toast:
//   toast.success("Contact added")
//   toast.error("Campaigns could not be loaded", { action: { label: "Retry", onClick: reload } })

const TONE = { success: "success", error: "error" };

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6.25" />
    <path d="M5.3 8.2l1.8 1.8 3.6-3.9" />
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6.25" />
    <path d="M8 4.9v3.6M8 11.1v.01" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6.25" />
    <path d="M8 7.4v3.7M8 4.9v.01" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M3 3l6 6M9 3l-6 6" />
  </svg>
);

const iconFor = (t) => {
  if (t.icon) return t.icon;
  if (t.type === "success") return <CheckIcon />;
  if (t.type === "error") return <ErrorIcon />;
  if (t.type === "loading")
    return <span className="sg-btn-spinner" aria-hidden="true" />;
  // Info (a plain toast("…")): a neutral "i", so the three tones read apart
  // at a glance even before the edge rule.
  if (t.type === "blank") return <InfoIcon />;
  return null;
};

export const SignalToast = ({ t }) => {
  const icon = iconFor(t);
  const finite =
    Number.isFinite(t.duration) && t.duration > 0 && t.type !== "loading";
  return (
    <div
      className="sg-toast"
      data-tone={TONE[t.type] || "neutral"}
      data-state={t.visible ? "open" : "closing"}
      {...t.ariaProps}
    >
      {icon && <span className="sg-toast-icon">{icon}</span>}
      <p className="sg-toast-msg">{resolveValue(t.message, t)}</p>
      {t.action?.label && (
        <button
          type="button"
          className="sg-toast-action"
          onClick={() => {
            t.action.onClick?.();
            toast.dismiss(t.id);
          }}
        >
          {t.action.label}
        </button>
      )}
      <button
        type="button"
        className="sg-toast-close"
        aria-label="Dismiss notification"
        onClick={() => toast.dismiss(t.id)}
      >
        <CloseIcon />
      </button>
      {finite && t.visible && (
        <span
          className="sg-toast-progress"
          style={{ animationDuration: `${t.duration}ms` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

const SignalToaster = ({
  position = "bottom-right",
  containerStyle,
  ...props
}) => (
  <Toaster
    position={position}
    gutter={8}
    containerClassName="sg-toaster"
    containerStyle={
      containerStyle ?? {
        right: 16,
        // Above the call dock (--sg-dock-clear grows with the dock bar) and
        // above a sticky save bar when one is showing (--sg-toast-lift).
        bottom: "calc(var(--sg-dock-clear, 64px) + var(--sg-toast-lift, 4px))",
      }
    }
    toastOptions={{
      duration: 4000,
      error: { duration: 6000 },
    }}
    {...props}
  >
    {(t) => <SignalToast t={t} />}
  </Toaster>
);

export default SignalToaster;
